'use client'

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { useLoanComparatorStore, useHydration } from '@/app/stores/loan/loan-comparator.store'
import { ILoanQuote, IQuotationFormOptions, IGlobalComparatorOptions } from '@/app/models/loanQuote'
import { collectedFromQuery, isQuoteReplaced, pendingPrefill, runChatPrefill } from '@/app/lib/guideChat/prefill'
import { applyPromoCode } from '@/app/endpoints/loanComparatorEndpoints'
import { clearPromoCodeCookie, isLoanPromoCodeEnabled, readPromoCodeCookie } from '@/app/utils/loan/promo-code'

// A ?promo= link lands on any page: the code waits in a cookie until a quote exists, then is applied once
const applyPromoCodeFromCookie = (quote: ILoanQuote) => {
    if (!isLoanPromoCodeEnabled() || quote.promo_code) return
    const code = readPromoCodeCookie()
    if (!code) return
    clearPromoCodeCookie()
    applyPromoCode(quote.token, code)
        .then((result) => {
            if (!result.valid) return
            useLoanComparatorStore.getState().quoteActions.update({
                promo_code: {
                    code: result.code ?? code,
                    label: result.label,
                    discount_type: result.discountType ?? 'fixed_amount',
                    discount_value: result.discountValue ?? 0,
                },
                promo_discount_amount: result.discountAmount,
            })
        })
        .catch((error) => console.error('[LoanComparatorStoreInitializer] Promo code from link failed:', error))
}

interface Props {
    loanQuote?: ILoanQuote | null
    formOptions?: IQuotationFormOptions | null
    globalOptions?: IGlobalComparatorOptions | null
}

export default function LoanComparatorStoreInitializer({ loanQuote, formOptions, globalOptions }: Props) {
    const setQuote = useLoanComparatorStore((state) => state.quoteActions.setQuote)
    const setFormOptions = useLoanComparatorStore((state) => state.formOptionsActions.setFormOptions)
    const setGlobalOptions = useLoanComparatorStore((state) => state.globalOptionsActions.setGlobalOptions)
    const setServerDataLoaded = useLoanComparatorStore((state) => state.setServerDataLoaded)

    // Wait for hydration to complete before setting server data
    const hasHydrated = useHydration()

    // Track the quote key to detect changes (based on ID and token)
    const quoteKey = loanQuote ? `${loanQuote.id}-${loanQuote.token}` : null
    const prevQuoteKeyRef = useRef<string | null>(null)
    const hasSetServerDataRef = useRef(false)

    const searchParams = useSearchParams()
    const chatSearch = searchParams.has('chat') ? `?chat=${encodeURIComponent(searchParams.get('chat') ?? '')}` : ''

    useEffect(() => {
        if (!hasHydrated) {
            return
        }

        // Form options and global options should always be set (they're static)
        if (formOptions) {
            setFormOptions(formOptions)
        }
        if (globalOptions) {
            setGlobalOptions(globalOptions)
        }

        const isNewServerQuote =
            !!loanQuote && !!loanQuote.token && (!hasSetServerDataRef.current || quoteKey !== prevQuoteKeyRef.current)
        if (isNewServerQuote) {
            setQuote(loanQuote)
            prevQuoteKeyRef.current = quoteKey
            hasSetServerDataRef.current = true
            applyPromoCodeFromCookie(loanQuote)
        }
        const mustFlagServerData = isNewServerQuote || !loanQuote

        if (!chatSearch && !pendingPrefill()) {
            if (mustFlagServerData) setServerDataLoaded(true)
            return
        }

        // The forms snapshot the store on mount, so the chat prefill must land before the wizard renders.
        const prefill = chatSearch ? collectedFromQuery(chatSearch) : Promise.resolve(null)
        const finish = () => {
            try {
                runChatPrefill(useLoanComparatorStore)
            } catch (error) {
                console.error('[LoanComparatorStoreInitializer] Chat prefill failed:', error)
            }
            if (mustFlagServerData) setServerDataLoaded(true)
        }
        prefill.then(finish, finish)
    }, [
        hasHydrated,
        loanQuote,
        quoteKey,
        formOptions,
        globalOptions,
        chatSearch,
        setQuote,
        setFormOptions,
        setGlobalOptions,
        setServerDataLoaded,
    ])

    // A server refresh of the quote wipes the unsaved prefill; reapplying inside the subscriber lands before React renders
    useEffect(
        () =>
            useLoanComparatorStore.subscribe((state, prev) => {
                if (isQuoteReplaced(state.quote, prev.quote)) runChatPrefill(useLoanComparatorStore)
            }),
        []
    )

    // This component doesn't render anything
    return null
}
